import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { L } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!r.ok) throw new Error("invalid");
      setDone(true);
    } catch {
      setError(L({ en: "This reset link is invalid or has expired. Please request a new one.", it: "Questo link non è valido o è scaduto. Richiedine uno nuovo." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-104px)] items-center justify-center px-6 py-20" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
        <p className="mono-tech mb-4 text-[#A8853B]">{L({ en: "Client Area", it: "Area Clienti" })}</p>
        <h2 className="font-display text-3xl text-[#0E3B33] sm:text-4xl">{L({ en: "Choose a new password.", it: "Scegli una nuova password." })}</h2>
        {!token && (
          <p className="mt-6 text-sm text-red-700" data-testid="reset-no-token">
            {L({ en: "Missing reset token — use the link from your email.", it: "Token mancante — usa il link ricevuto via email." })}
          </p>
        )}
        {token && !done && (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
            <input required type="password" minLength={8} data-testid="reset-password-input" className="forge-field" placeholder={L({ en: "New password (min 8 characters)", it: "Nuova password (min 8 caratteri)" })} value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && <p className="text-sm text-red-700" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="reset-submit-btn" className="ember-btn w-full justify-center disabled:opacity-60">
              {busy ? L({ en: "Saving…", it: "Salvataggio…" }) : L({ en: "Set new password", it: "Imposta nuova password" })}
            </button>
          </form>
        )}
        {done && (
          <div className="mt-8 rounded-md border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-5 text-sm text-[#4A5248]" data-testid="reset-success">
            {L({ en: "Password updated. You can sign in with it now.", it: "Password aggiornata. Ora puoi accedere." })}
          </div>
        )}
        <p className="mt-6 text-center text-xs text-[#8A8F82]">
          <Link to="/login" data-testid="reset-back-login" className="font-medium text-[#A8853B] hover:text-[#0E3B33]">
            {L({ en: "Back to sign in", it: "Torna all'accesso" })}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
